import React, { useState, useEffect, useRef } from 'react'
import FormInput from '../components/FormInput'
import AppBar from '../components/AppBar'
import { useNoKeyboardResize } from '../hooks/useNoKeyboardResize'
import { useNavigate, useLocation } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import { ImportedPlaceSummary } from '../api/bookmark'
import { bookmarkApi, ImportGroup } from '../api/bookmark'
import ImportGroupPicker from '../components/ImportGroupPicker'
import SaveDestinationSheet from '../components/SaveDestinationSheet'
import { takeAfterCreate } from '../utils/platelistCreateReturn'
import { useImportGroupSelection } from '../hooks/useImportGroupSelection'
import { useOnboardingRankStore } from '../stores/onboardingRankStore'
import { toastUtil } from '../utils/toast'
import { registerOpenSheet } from '../utils/sheetOpenState'
import { firstUrlIn } from '../utils/sharedLink'
import ImportPlaceSearchResults from '../components/ImportPlaceSearchResults'

/**
 * 온보딩이 가입 흐름의 마지막 단계 → 완료 시 홈(홈에서 튜토리얼 자동 노출).
 *
 * filter=recommended를 붙이는 이유: 홈은 파라미터가 없으면 팔로잉을 기본으로 삼고,
 * 세션에 남은 지난 탭으로 되돌아가기도 한다. 그러면 건너뛴 직후 어느 탭이 뜰지
 * 그때그때 달라진다. 갓 가입한 사람은 팔로잉이 비어 있으니 추천으로 고정한다.
 */
const NEXT_PATH = '/main-home?filter=recommended'

/**
 * 첫 가입 온보딩(연락처 연동보다 먼저): 네이버/카카오 지도 리스트 링크를 붙여넣어
 * 가본 곳을 골라 순서대로 기록(랭킹)한다. 랭킹은 기존 StoreReview를 redirect 체이닝으로 재사용.
 */
/** 체크리스트를 한 번에 몇 개까지 그릴지 */
const PAGE_SIZE = 40

const OnboardingImportRank: React.FC = () => {
  const navigate = useNavigate()
  useNoKeyboardResize()
  const location = useLocation()
  const start = useOnboardingRankStore((s) => s.start)
  const stopRank = useOnboardingRankStore((s) => s.stop)
  const reset = useOnboardingRankStore((s) => s.reset)
  const active = useOnboardingRankStore((s) => s.active)
  const setFromHome = useOnboardingRankStore((s) => s.setFromHome)

  // 완료/건너뛰기 후 이동: 홈 로고로 들어왔으면 홈, 온보딩이면 연락처 연동으로.
  // (fromHome은 스토어에 있어 store-review 체이닝을 거쳐도 유지됨)
  const goAfter = () => {
    const fromHome = useOnboardingRankStore.getState().fromHome
    reset()
    // 홈 로고로 들어왔든 온보딩이든, 나갈 때는 항상 추천 피드로 보낸다
    navigate(fromHome ? '/main-home?filter=recommended' : NEXT_PATH)
  }

  const [url, setUrl] = useState('')
  const [isResolving, setIsResolving] = useState(false)
  // 묶음이 나뉜 링크(네이버 기획전)일 때만 쓰인다
  const pick = useImportGroupSelection()
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  // 첫 조회에서 받은 전체 장소. 묶음을 고른 뒤 여기서 걸러 쓴다.
  const [pendingPlaces, setPendingPlaces] = useState<
    ImportedPlaceSummary[] | null
  >(null)
  /*
   * 한 번에 그릴 개수.
   *
   * 기획전을 가져오면 500곳이 넘는다. 전부 DOM 에 올리면 웹뷰가 멈춰
   * 아무것도 눌리지 않는다(실기기에서 확인). 선택은 전체에 그대로 걸리고,
   * **그리는 것만** 나눈다.
   */
  const [isSaving, setIsSaving] = useState(false)
  const [isSaveSheetOpen, setIsSaveSheetOpen] = useState(false)
  const [savedPlaceIds, setSavedPlaceIds] = useState<number[]>([])
  /** 방금 만들고 돌아온 리스트 — 시트가 담고 체크한다 */
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null)

  /** 리스트를 새로 만들고 돌아왔으면 시트를 다시 연다 */
  useEffect(() => {
    const back = takeAfterCreate('onboarding-rank')
    if (!back) return
    const ids = (back.payload as { placeIds?: number[] } | undefined)?.placeIds ?? []
    setSavedPlaceIds(ids)
    setIsSaveSheetOpen(true)
    setJustCreatedId(back.createdId)
  }, [])
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  /** 체크리스트에서 뒤로가기를 눌렀을 때 돌아갈 묶음 화면 */
  const [lastGroups, setLastGroups] = useState<{
    groups: ImportGroup[]
    title: string | null
    places: ImportedPlaceSummary[]
  } | null>(null)
  const [places, setPlaces] = useState<ImportedPlaceSummary[]>([])
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())
  const launchedRef = useRef(false)
  const triedUrlRef = useRef('')

  // 큐의 다음 장소를 StoreReview로 띄움(자기 자신으로 복귀하는 redirect 포함). 끝나면 다음 단계로.
  const launchNext = () => {
    const { placeIds, cursor } = useOnboardingRankStore.getState()
    if (cursor >= placeIds.length) {
      goAfter()
      return
    }
    const placeId = placeIds[cursor]
    useOnboardingRankStore.getState().setCursor(cursor + 1)
    navigate(
      `/store-review/${placeId}?redirect=${encodeURIComponent(
        '/onboarding-rank'
      )}`
    )
  }

  /**
   * 기록 줄을 끊고, 떠나기 전 화면을 되살린다.
   *
   * 되살리는 이유 — 잘못 눌러 들어온 사람에게 링크부터 다시 넣으라고 하면
   * 대가가 너무 크다. 489곳짜리 기획전에서 지역을 고르고 상호를 하나씩
   * 체크한 것이 전부 날아간다.
   *
   * 몇 곳을 기록했는지도 알려 준다 — 뒤로 나왔을 때 앞서 쓴 기록이 사라진
   * 것처럼 보이면 안 된다. 한 곳도 안 썼으면 아무 말도 하지 않는다.
   */
  const resumeFromRanking = () => {
    const { resume, done } = useOnboardingRankStore.getState()
    stopRank()

    if (resume) {
      if (resume.groups) {
        pick.start(resume.groups, resume.title, resume.selected, {
          groupKind: resume.groupKind,
          ownerName: resume.ownerName,
        })
        if (resume.openedGroupId) {
          pick.setOpenedGroupId(resume.openedGroupId)
        }
      }
      setPendingPlaces(resume.pendingPlaces)
      setPlaces(resume.places)
      setCheckedIds(new Set(resume.groups ? [] : resume.selected))
      setLastGroups(resume.lastGroups)
    }

    if (done > 0) {
      toastUtil.success(`${done}곳을 기록했어요`)
    }
  }

  /**
   * 기록 줄에서 이 화면으로 돌아왔을 때.
   *
   * 한 곳을 **마치고** 왔는지, 리뷰 화면에서 '<' 로 **빠져나왔는지** 가른다.
   * 예전에는 둘 다 똑같이 /onboarding-rank 로 와서 구분할 수가 없었고,
   * 그래서 잘못 눌러 들어간 사람이 '<' 를 눌러도 '다음 장소' 로 읽혀,
   * 고른 곳들의 리뷰 화면이 줄줄이 지나간 끝에 홈으로 튕겼다.
   *
   * 완료는 ReviewComplete 가 rankStep=done 을 붙여 알려 준다. 그게 없으면
   * 빠져나온 것이다.
   */
  useEffect(() => {
    if (launchedRef.current) return

    if (useOnboardingRankStore.getState().active) {
      const isDone =
        new URLSearchParams(location.search).get('rankStep') === 'done'

      if (!isDone) {
        resumeFromRanking()
        return
      }

      // store-review 체이닝 복귀 — 진입모드는 스토어에 유지되어 있으므로 건드리지 않음
      useOnboardingRankStore.getState().markDone()
      launchedRef.current = true
      /*
       * 다음 장소로 떠나기 전에 주소에서 완료 표시를 뗀다.
       *
       * 떼지 않으면 이 히스토리 칸이 rankStep=done 인 채로 남아, 다음 장소에서
       * '<' 를 눌러 여기로 돌아왔을 때 또 '완료' 로 읽힌다 — 고치려던 바로
       * 그 증상이 두 번째 장소부터 되살아난다.
       */
      navigate('/onboarding-rank', { replace: true })
      launchNext()
    } else {
      // 프레시 진입: 홈 로고면 fromHome=true(완료 후 홈), 온보딩이면 false(연락처 연동)
      const st = location.state as { fromHome?: boolean } | null
      setFromHome(!!st?.fromHome)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  /**
   * 가져오기 응답에 실려 온 장소를 목록에 붙인다.
   *
   * 예전에는 place_ids만 받아 장소마다 상세 API를 다시 불렀다(400곳이면 400번).
   * 브라우저가 동시 연결을 6개쯤으로 제한해 실제로는 여러 겹으로 나뉘어 돌았고,
   * 그래서 "N곳을 가져왔어요" 토스트가 뜬 뒤에도 스피너가 한참 더 돌았다.
   * 이 화면은 이름·주소만 쓰는데 상세 API는 영업시간·북마크 수·리뷰 키워드까지
   * 계산해 내려주므로 낭비도 컸다.
   */
  const appendPlaces = (incoming: ImportedPlaceSummary[]) => {
    if (incoming.length === 0) return
    setPlaces((prev) => {
      const seen = new Set(prev.map((p) => p.id))
      return [...prev, ...incoming.filter((p) => !seen.has(p.id))]
    })
  }

  // raw를 주면 그걸로, 안 주면 입력칸 값으로 실행한다.
  // 붙여넣기 직후에는 setUrl이 아직 반영되지 않아 값을 직접 넘겨야 한다.
  /**
   * 고른 묶음의 장소만 목록에 더한다. 서버를 다시 부르지 않는다.
   *
   * 이름·주소는 첫 조회 때 받아 둔 pendingPlaces 에서 가져온다. 묶음에는
   * id 만 들어 있어서, 그것만으로 목록을 만들면 체크리스트가 빈 줄이 된다.
   */
  /*
   * 이 화면에서는 스와이프 뒤로가기를 끈다.
   *
   * 라우트 하나 안에 단계가 넷이라(링크 입력 → 묶음 목록 → 묶음 안 →
   * 상호 고르기) 스와이프가 단계를 모르고 화면을 통째로 떠났다. 가짜
   * 히스토리를 쌓아 되짚게 해 봤는데, 라우트가 없는 자리라 **빈 화면이
   * 떴다.** 되돌릴 자리를 만드는 대신 제스처를 끄고, 상단 '<' 버튼
   * (handleSkip)만 단계를 되짚게 한다.
   *
   * 시트가 열려 있을 때 쓰는 기존 장치를 그대로 쓴다 —
   * useSwipeBack 이 isAnySheetOpen() 을 보고 비켜 준다.
   */
  useEffect(() => registerOpenSheet(), [])

  /**
   * 고른 곳을 어디에 담을지 고르게 한다.
   *
   * 링크를 넣을 때마다 플레이트리스트를 새로 만들면 금방 지저분해진다.
   * 대부분은 '저장한 장소' 에만 넣고 싶을 뿐이라, 거기로 보낼지
   * 플레이트리스트에 넣을지 묻는다.
   */
  const handleSavePicked = async () => {
    if (isSaving || selectedIds.length === 0) return

    setIsSaving(true)
    try {
      await bookmarkApi.savePlaces(selectedIds)
      setSavedPlaceIds(selectedIds)
      // 알림은 시트를 닫을 때 한 번만 (아직 리스트를 고르는 중이다)
      setIsSaveSheetOpen(true)
    } catch (error) {
      console.error('저장 실패:', error)
      // 메시지는 apiClient 인터셉터가 띄운다
    } finally {
      setIsSaving(false)
    }
  }

  /** 시트를 닫으면 다음 단계로 */
  const handleSheetClose = ({ pickedAny }: { pickedAny: boolean }) => {
    setIsSaveSheetOpen(false)
    // 리스트에 담았으면 그때마다 "○○에 담았어요" 를 봤다. 요약까지 띄우면
    // 두 토스트가 겹친다. 아무것도 안 고르고 닫은 경우에만 알린다.
    if (!pickedAny && savedPlaceIds.length > 0) {
      toastUtil.success(`${savedPlaceIds.length}곳을 저장했어요`)
    }
    pick.reset()
    setPendingUrl(null)
    setPendingPlaces(null)
    goAfter()
  }

  const handleResolve = async (raw?: string) => {
    const trimmed = (raw ?? pendingUrl ?? url).trim()
    if (!trimmed || isResolving) return
    // 주소가 없으면 링크가 아니라 가게 이름이다 — 아래 검색 결과가 맡는다
    if (!firstUrlIn(trimmed)) return
    setIsResolving(true)
    triedUrlRef.current = trimmed
    try {
      const res = await bookmarkApi.resolveSharedLink(trimmed)

      /*
       * 지역을 고를 수 있는 링크. 서버가 이미 다 찾아 뒀고 묶음만 받았다.
       * 고른 뒤에는 서버를 다시 부르지 않고 여기서 거른다.
       */
      if (res.needsGroupChoice && res.groups?.length) {
        pick.start(res.groups, res.folder_name, [], {
          groupKind: res.groupKind,
          ownerName: res.ownerName,
        })
        setPendingUrl(trimmed)
        setPendingPlaces(res.places ?? [])
        setUrl('')
        return
      }

      setUrl('')
      pick.reset()
      setPendingUrl(null)
      if (res.place_ids.length === 0) {
        toastUtil.info('가져올 장소가 없어요')
      } else if (res.imported < res.total) {
        toastUtil.success(`${res.total}곳 중 ${res.imported}곳을 가져왔어요`)
      } else {
        toastUtil.success(`${res.imported}곳을 가져왔어요`)
      }
      appendPlaces(res.places ?? [])
    } catch (error) {
      // 에러 토스트는 apiClient 인터셉터가 처리 (중복 방지)
      // 다만 같은 링크를 다시 시도할 수는 있어야 한다 — 안 그러면 한 번
      // 실패한 링크는 지웠다 다시 넣어도 아무 반응이 없다.
      triedUrlRef.current = ''
    } finally {
      setIsResolving(false)
    }
  }

  /**
   * 붙여넣은 것이 지도 공유 링크로 보이는지.
   *
   * 서버가 받아주는 범위에 맞춘다. 서버는 붙여넣은 글에서 첫 http(s) 주소를
   * 뽑고, 단축 주소를 따라간 뒤 'naver' 또는 'kakao'가 들어 있으면 처리한다
   * (MapListImportService::fetchSharedList).
   *
   * 예전에는 프론트가 naver.me / kko.to / map.naver.com / map.kakao.com /
   * place.map.kakao 다섯 개만 인정했다. 서버는 받아주는데 화면이 막고 있어서,
   * v.kakaomap.com이나 m.place.naver.com 같은 주소를 넣으면 **아무 일도
   * 일어나지 않았다** — 실행 버튼도 없어서 손쓸 방법이 없었다.
   */
  const looksLikeMapLink = (text: string) => {
    const m = text.match(/https?:\/\/\S+/i)
    if (!m) return false
    return /(naver|kakao|kko)/i.test(m[0])
  }

  /**
   * 공유 링크를 붙여넣으면 자동으로 가져오기 실행.
   *
   * 예전에는 값이 바뀔 때마다(useEffect [url]) 실행했다. 그러면 지우는
   * 동안에도 한 글자에 요청이 하나씩 나갔다 — 실제로 한 사용자가 60초에
   * 33건을 보냈다. 막아주는 장치가 셋 있었지만 전부 소용이 없었다:
   * isResolving은 요청 중에만 막고, looksLikeMapLink는 'https://naver'
   * 같은 미완성 문자열도 통과시키고, triedUrlRef는 한 글자만 달라도
   * 다른 문자열로 봤다. 그 사이 실패 토스트가 계속 떠서 사용자는 무엇이
   * 잘못됐는지 더 알기 어려웠다.
   *
   * 붙여넣은 글로 입력칸을 통째로 바꾸는 것도 의도된 동작이다. 실패하면
   * 입력칸에 글이 그대로 남는데 커서가 끝에 있어서, 다시 붙여넣으면 앞
   * 주소 뒤에 공백 없이 덧붙어 주소가 망가졌다.
   *
   * 못 알아본 링크는 키보드의 완료(form submit)로 직접 실행할 수 있다.
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text')
    if (!pasted) return
    e.preventDefault()
    setUrl(pasted)

    const trimmed = pasted.trim()
    if (looksLikeMapLink(trimmed) && trimmed !== triedUrlRef.current) {
      handleResolve(trimmed)
    }
  }

  /*
   * 입력칸에 주소가 없으면 가게 이름 검색으로 본다.
   * 링크를 못 구한 사람도(지도 앱이 없거나 리스트가 비공개) 한 곳씩 담을 수 있게.
   */
  const searchKeyword =
    !pick.groups && !isResolving && url.trim() && !firstUrlIn(url) ? url : ''

  /** 검색에서 고른 가게를 체크리스트에 더하고 체크해 둔다 */
  const handlePickSearched = (place: ImportedPlaceSummary) => {
    appendPlaces([place])
    setCheckedIds((prev) => new Set(prev).add(place.id))
    setUrl('')
    toastUtil.success(`'${place.name}' 담았어요`)
  }

  const toggleCheck = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const allChecked = places.length > 0 && checkedIds.size === places.length
  const toggleAll = () => {
    setCheckedIds(allChecked ? new Set() : new Set(places.map((p) => p.id)))
  }

  /*
   * 지금 고른 것.
   *
   * 묶음을 보는 중이면 묶음에서 고른 상호, 아니면 체크리스트에서 고른 것.
   * 하단 버튼 둘은 어느 단계든 **이것**을 가지고 움직인다 — 그래야 버튼이
   * 단계마다 늘어나지 않는다.
   */
  const selectedIds = pick.groups ? pick.selected : Array.from(checkedIds)

  /**
   * 고른 곳을 한 곳씩 기록하러 간다.
   *
   * 묶음에서 바로 갈 수도 있다. 예전에는 묶음 → 체크리스트 → 기록 이었는데,
   * 묶음 안에서 이미 상호를 하나씩 고르므로 체크리스트가 같은 일을 두 번
   * 하는 셈이었다(버튼도 그래서 셋이 됐다).
   */
  const handleStartRanking = () => {
    if (selectedIds.length === 0) {
      toastUtil.info('기록할 장소를 먼저 선택해주세요')
      return
    }
    /*
     * 지금 화면을 같이 담아 둔다. 뒤로 나왔을 때 이걸로 되살린다.
     * (어느 단계였는지까지 — 묶음 목록, 묶음 안, 체크리스트)
     */
    start(selectedIds, {
      groups: pick.groups,
      title: pick.title,
      groupKind: pick.groupKind,
      ownerName: pick.ownerName,
      openedGroupId: pick.openedGroupId,
      pendingPlaces,
      places,
      selected: selectedIds,
      lastGroups,
    })
    launchedRef.current = true
    launchNext()
  }

  /**
   * 뒤로가기.
   *
   * 이 화면은 안에 단계가 셋이다 — 링크 입력 → 묶음 고르기 → 장소 체크리스트.
   * 예전에는 어느 단계든 바로 화면을 나가서, 묶음을 고른 뒤 "다시 고르고
   * 싶다" 가 안 됐다. 한 단계씩 되짚고, 맨 앞에서만 나간다.
   */
  const handleSkip = () => {
    // 묶음 안을 보는 중 → 묶음 목록으로
    if (pick.openedGroupId) {
      pick.setOpenedGroupId(null)
      return
    }

    // 묶음 고르는 중 → 링크 입력으로
    if (pick.groups) {
      pick.reset()
      setPendingUrl(null)
      return
    }

    // 체크리스트 보는 중이고 돌아갈 묶음이 있으면 → 묶음 고르기로
    if (lastGroups && places.length > 0) {
      pick.start(lastGroups.groups, lastGroups.title)
      setPendingPlaces(lastGroups.places)
      setPlaces([])
      setCheckedIds(new Set())
      setLastGroups(null)
      return
    }

    goAfter()
  }

  // 기록 진행 중(다음 장소로 체이닝 중)에는 setup UI 깜빡임 방지 — 스피너만
  /*
   * 담기 시트는 로딩 가드보다 먼저. 리스트를 만들고 돌아오면 아직 불러오는
   * 중일 수 있는데, 그때 시트가 같이 사라지면 빈 화면만 남는다.
   */
  const sheet = (
    <SaveDestinationSheet
      isOpen={isSaveSheetOpen}
      onClose={handleSheetClose}
      placeIds={savedPlaceIds}
      justCreatedCategoryId={justCreatedId}
      returnKey="onboarding-rank"
    />
  )

  if (active) {
    return (
      <>
        <div className="app-container w-full h-full flex items-center justify-center bg-backgroundColorBg2">
          <LoadingSpinner />
        </div>
        {sheet}
      </>
    )
  }

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      {/*
        제목은 다른 화면들처럼 앱바에 둔다(아래 선 포함). 홈 로고로 들어온
        사람에게 이 화면만 제목 모양이 달라 보이면 안 된다.

        뒤로가기는 '건너뛰기'와 같은 길(goAfter)로 보낸다. 기본 동작
        (한 칸 뒤로)이면 온보딩으로 들어온 사람이 가입 흐름으로 되돌아간다.
      */}
      <AppBar
        title="장소 가져오기"
        isBottomBorder
        onBack={handleSkip}
      />
      <div className="app-container flex-1 flex flex-col px-20px pt-20px overflow-hidden">

        {/*
          플레이트리스트의 '장소 가져오기'(ImportListContent)와 같은 모양이다.

          묶음을 고르는 동안에는 이 form 이 스크롤을 맡는다. 바깥 컨테이너가
          overflow-hidden 이고 스크롤 영역은 아래 체크리스트 쪽에 따로 있어서,
          묶음 목록이 길어지면 **잘린 채 스크롤이 안 됐다**(지역이 18개까지
          나온다). 고를 때만 flex-1 + overflow-y-auto 를 준다.
        */}
        <form
          className={
            pick.groups ? 'flex-1 min-h-0 overflow-y-auto scrollbar-hide' : ''
          }
          onSubmit={(e) => {
            e.preventDefault()
            handleResolve()
          }}
        >
          {pick.groups ? (
            <>
              <ImportGroupPicker
                title={pick.title}
                groups={pick.groups}
                places={pendingPlaces ?? []}
                selectedPlaceIds={pick.selected}
                onTogglePlace={pick.togglePlace}
                onToggleGroup={pick.toggleGroup}
                onToggleAll={pick.toggleAll}
                openedGroupId={pick.openedGroupId}
                onOpenGroup={pick.setOpenedGroupId}
                groupKind={pick.groupKind}
                ownerName={pick.ownerName}
              />
            </>
          ) : (
            <>
              {/* 라벨은 FormInput 에 맡긴다 — 크기(16px)와 간격(10px)이 앱 표준을 따라간다 */}
              {/*
                inputMode="url" 은 뺐다. 가게 이름도 받는데, URL 키보드로는
                한글을 치기가 불편하다.
              */}
              <FormInput
                label="지도 링크 · 가게 이름"
                name="importUrl"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onPaste={handlePaste}
                placeholder="리스트는 지도 앱에서 '공개'로 바꾼 후 복사하기"
                marginBottom={0}
              />

              {/*
                '가져오기' 버튼은 두지 않는다(2026-10-25 결정). 붙여넣으면 바로
                실행되고, 못 알아본 링크는 키보드의 완료(이 form 의 submit)로
                다시 실행할 수 있다.
              */}
            </>
          )}
        </form>

        {/*
          체크리스트 (링크 가져온 후).

          묶음을 고르는 동안에는 아예 그리지 않는다. flex-1 이라 내용이
          없어도 화면 절반을 차지해, 묶음 목록 아래에 빈 상자가 생겼다.
        */}
        <div
          className={
            // 묶음을 고르는 중에는 숨긴다. 다만 가져오는 동안에는 여기서
            // 스피너가 돌아야 하므로 그때는 보여 준다.
            pick.groups && !isResolving
              ? 'hidden'
              : 'mt-20px flex-1 min-h-0 overflow-y-auto scrollbar-hide'
          }
        >
          {isResolving ? (
            /*
              가져오는 동안의 스피너.

              py-40px 로 두면 글자 수에 따라 위치가 흔들린다. 자리를 꽉 채우고
              그 안에서 가운데에 두면 어느 기기에서든 화면 중앙으로 온다
              (index.css 의 empty-state-center 설명과 같은 이유).
            */
            <div className="w-full h-full relative">
              <div className="absolute inset-0 flex items-center justify-center empty-state-center">
                <LoadingSpinner />
              </div>
            </div>
          ) : searchKeyword ? (
            // 빈 결과 문구가 이 칸 가운데에 오도록 자리를 채운다 (스피너와 같은 방식)
            <div className="relative min-h-full">
              <ImportPlaceSearchResults
                keyword={searchKeyword}
                onPick={handlePickSearched}
              />
            </div>
          ) : places.length > 0 ? (
            <>
              <button
                type="button"
                onClick={toggleAll}
                className="mb-8px text-14px text-4B3621-80% font-bold"
              >
                {allChecked ? '전체 해제' : '전체 선택'}
              </button>
              <div className="flex flex-col gap-8px pb-16px">
                {places.slice(0, visibleCount).map((p) => {
                  const checked = checkedIds.has(p.id)
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleCheck(p.id)}
                      className="w-full flex items-center gap-12px px-12px py-12px rounded-12px bg-472410-5% text-left"
                    >
                      <img
                        src={
                          checked
                            ? '/images/checkbox-bold-filled.png'
                            : '/images/checkbox-bold-unchecked.png'
                        }
                        alt=""
                        className="flex-shrink-0 w-22px h-22px object-contain"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-style-button2 text-brown-80%-main text-ellipsis-1">
                          {p.name}
                        </span>
                        <span className="block text-13px text-textColorText3 text-ellipsis-1">
                          {p.address ?? ''}
                        </span>
                      </span>
                    </button>
                  )
                })}
                {places.length > visibleCount && (
                  <button
                    type="button"
                    onClick={() =>
                      setVisibleCount((n) => n + PAGE_SIZE)
                    }
                    className="w-full py-12px rounded-12px bg-472410-5% text-14px text-4B3621-80% font-bold"
                  >
                    더 보기 ({places.length - visibleCount}곳 남음)
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/*
        하단 버튼. 어느 단계든 **둘뿐**이고 자리도 고정이다.

          저장하기  고른 곳을 어디에 담을지 묻는다 (저장함 / 플레이트리스트)
          기록하기  한 곳씩 순위를 매기러 간다

        나가기는 앱바 뒤로가기가 맡는다. 링크를 아직 안 넣었으면 고른 것이
        없으므로 둘 다 꺼진 채로 둔다 — 자리가 들쭉날쭉하지 않게.
      */}
      <div className="app-container px-20px pb-24px pt-8px flex flex-col items-center gap-16px">
        <button
          type="button"
          onClick={handleSavePicked}
          disabled={isSaving || isResolving || selectedIds.length === 0}
          className={`w-full py-16px rounded-full font-pretendard font-700 text-16px ${
            isSaving || isResolving || selectedIds.length === 0
              ? 'bg-E8E1D9 text-[#918063]'
              : 'bg-4B3621-80% text-white'
          }`}
        >
          저장하기
        </button>

        <button
          type="button"
          onClick={handleStartRanking}
          disabled={isSaving || isResolving || selectedIds.length === 0}
          className="font-pretendard font-700 text-16px text-brown-80%-main disabled:opacity-40"
        >
          기록하기
        </button>
      </div>

      {/*
        어디에 담을지 고르는 시트.
        고르지 않고 닫으면 아무 일도 안 일어난다 — 고른 것은 그대로 남는다.
      */}
      {sheet}
    </div>
  )
}

export default OnboardingImportRank
